/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useUserProfileSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom hook for submitting user profile updates.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useDebugModeStore } from '../../../store/debugMode';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import { updateUserProfile } from '../api';
import type { UserProfileData } from '../types';

/**
 * Custom hook for submitting user profile
 */
export function useUserProfileSubmit(
  formData: UserProfileData,
  avatar: string | null,
  debugModePermission: boolean
) {
  const { t } = useI18n();
  const { user, updateUser } = useAuthStore();
  const { addNotification } = useNotifications();
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!user?.id) {
      addNotification({ message: t('profile.userNotFound'), type: 'error' });
      return;
    }

    setSaving(true);
    try {
      // Make API call to update user profile
      await updateUserProfile(user.id, {
        email: formData.email,
        fullName: formData.fullName,
        department: formData.department,
        locale: formData.locale,
        timezone: formData.timezone,
        startupDialog: formData.startupDialog,
        startModuleUrl: formData.startModuleUrl,
        debugModeActive: formData.debugModeActive
      });
      
      // Update user in store
      if (updateUser) {
        const updatedUser = {
          ...user,
          email: formData.email,
          fullName: formData.fullName,
          department: formData.department,
          locale: formData.locale,
          timezone: formData.timezone,
          startupDialog: formData.startupDialog,
          startModuleUrl: formData.startModuleUrl,
          avatar: avatar || user?.avatar,
          debugModeActive: formData.debugModeActive
        };
        
        updateUser(updatedUser as any);

        // Update debug mode store after successful save
        const debugModeActive = String(formData.debugModeActive) === 'true';
        useDebugModeStore.getState().setDebugMode(debugModeActive, debugModePermission);
        useDebugModeStore.getState().syncWithUser(updatedUser);
      }

      addNotification({ message: t('profile.profileUpdated'), type: 'success' });
    } catch (error) {
      addNotification({ message: t('profile.profileUpdateError'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return {
    handleSave,
    saving
  };
}

